/**
 * Подгряване на огледалото при вход (§14.6 №5 и §16.4 №3 на договора,
 * docs/edge.md §Огледало). Касата сервира без интернет САМО това, което е
 * видяла през `PUT {edge}/mirror`, а огледалото се пълни от отговорите на
 * самото приложение: екран, който никой не е отварял, докато е имало
 * интернет, липсва. Затова при вход с включен резерв се прави по един ТИХ
 * `GET` за белите пътища, които касата още не е виждала — иначе първият
 * офлайн час е с празен списък клиенти („Клиенти"/„Отстъпки"), без методи на
 * плащане, без сервитьори за „Прехвърли" и с кухненски монитор, който не
 * знае станциите си.
 *
 * Правила: само в режим `cloud` и само с билет; пропуска всичко, което вече
 * е в `GET {edge}/status`.`mirror` или касата е ПОТВЪРДИЛА в това зареждане;
 * грешките (липсващо право, стар BE) се гълтат — подгряването няма право да
 * забави или да счупи входа. Затова път, отказан на един потребител (напр.
 * терминалният акаунт без `select_customer`), се опитва пак при следващия
 * вход — със СЛЕДВАЩИЯ човек правата може да стигат.
 * Огледалният `PUT` към касата го прави response интерсепторът на
 * `api/client.ts`, както за всеки друг GET, а подгряването чака изхода му
 * (`config.edgeMirrorWrite`): успешният облачен GET сам по себе си НЕ значи,
 * че касата има данните (одит 0.0.290→0.0.321, R29). Пропаднал запис оставя
 * пътя чакащ — един повторен опит веднага, после при следващото подгряване.
 */
import { api } from "../../api/client";
import { getEdgeStatus } from "../../api/edge";
import { RESERVATIONS_EDGE_VIEW_PATH } from "../../api/reservations";
import { TIP_SETTINGS_PATH } from "../../api/tipSettings";
import { getRestaurantName } from "../restaurant";
import { devLog, getActiveEdgeTicket, getEdgeRuntime } from "./edgeRuntime";
import {
  getEdgeBaseUrl,
  getEdgeConfigState,
  isEdgeLocalMode,
} from "./edgeState";

interface WarmupRequest {
  path: string;
  params?: Record<string, string>;
}

// Колко чакаме Redux да научи кой е влязъл и през колко питаме: пътят на
// монитора носи неговото ид, а подгряването тръгва успоредно с
// `GET /user/info` и понякога го изпреварва (§16.4 №3).
const IDENTITY_WAIT_MS = 10_000;
const IDENTITY_POLL_MS = 500;

/** Пътищата, които касата ще трябва да сервира, а приложението може и да не
 * поиска, преди интернетът да падне. Всички са в белите префикси на
 * `edgeMirror.ts` (префикси или точни пътища) — иначе отговорът не би
 * стигнал до касата. */
const warmupRequests = (): WarmupRequest[] => {
  const restaurantName = getRestaurantName();
  return [
    { path: "/discounts/clients", params: { restaurant_name: restaurantName } },
    { path: "/payment-methods" },
    // Настройките за бакшиш — „Закрий" ги чете и без интернет (самата каса
    // проверява бакшиша по своя snapshot). По-стар BE отговаря 404 —
    // грешката се гълта като всяка друга тук.
    { path: TIP_SETTINGS_PATH },
    { path: "/user/users" },
    { path: "/menu/", params: { restaurant_name: restaurantName } },
    // Резервациите без интернет — само срещу каса, която ги умее (по-стара
    // каса би ги пазила като безполезен ред). После ги опреснява
    // `edgeReservationsFeed.ts`; тук е първото пълнене след вход.
    ...(reservationsOfflineNow()
      ? [{ path: RESERVATIONS_EDGE_VIEW_PATH }, { path: "/restaurant-schema" }]
      : []),
  ];
};

const reservationsOfflineNow = (): boolean =>
  getEdgeRuntime()?.store.getState().edge.reservationsOffline === true;

/**
 * Кухненският/барският екран (§16.4 №3). `GET /monitors/info/{uid}` е първото,
 * което `pages/monitors` иска, и без огледало на този път екранът показва
 * „Недостъпно без интернет": станциите му са неизвестни, значи не тръгва нито
 * един станционен WebSocket. Дотук пътят се огледваше само ако мониторът е бил
 * отварян, докато е имало интернет.
 *
 * Той е единственият бял път с ид в адреса, затова се сглобява отделно — чак
 * когато Redux знае кой е влязъл. Терминал без самоличност (общият акаунт X
 * преди ПИН-а) го пропуска: мониторът работи под свой акаунт и подгрява своя
 * път при неговия вход.
 */
async function monitorWarmupRequest(): Promise<WarmupRequest | null> {
  const userId = await waitForLoggedInUserId();
  return userId === null ? null : { path: `/monitors/info/${userId}` };
}

async function waitForLoggedInUserId(): Promise<number | null> {
  const runtime = getEdgeRuntime();
  if (!runtime) return null;
  const until = Date.now() + IDENTITY_WAIT_MS;
  for (;;) {
    const userId = runtime.store.getState().auth.userId;
    if (userId !== null) return userId;
    if (Date.now() >= until) return null;
    await new Promise((resolve) =>
      window.setTimeout(resolve, IDENTITY_POLL_MS),
    );
  }
}

// Един полет наведнъж (сесиите тръгват при всяка смяна на токена) и памет
// за пътищата, чийто запис касата е ПОТВЪРДИЛА в ТОВА зареждане (R29). Пътят
// остава тук, дори касата да не го изброява в `status.mirror` — изгледът на
// резервациите тя пази отделно и иначе щеше да се тегли при всяко подгряване.
let warmupInFlight = false;
const mirroredPaths = new Set<string>();

/** Опити на път в ЕДНО подгряване, когато облакът отговаря, а касата не записва. */
const MIRROR_ATTEMPTS_PER_WARMUP = 2;

/** Касата вече има отговор за този път (ключът е пътят, със или без query). */
const isMirrored = (
  mirror: Record<string, string> | undefined,
  path: string,
): boolean =>
  !!mirror &&
  Object.keys(mirror).some((key) => key === path || key.startsWith(`${path}?`));

export async function warmEdgeMirror(): Promise<void> {
  if (warmupInFlight || isEdgeLocalMode() || !getEdgeConfigState()?.enabled) {
    return;
  }
  const base = getEdgeBaseUrl();
  const ticket = getActiveEdgeTicket();
  if (!base || !ticket) return;

  warmupInFlight = true;
  try {
    let mirror: Record<string, string> | undefined;
    try {
      mirror = (await getEdgeStatus(base, ticket)).mirror;
    } catch (error) {
      // Касата не отговаря сега — при следващия вход опитваме пак.
      devLog("mirror warmup skipped — no status from the till", error);
      return;
    }

    for (const request of warmupRequests()) await warmPath(request, mirror);
    // Пътят на монитора е последен: чакането за самоличността (до 10 s на общ
    // терминал, където тя идва чак с ПИН-а) не бива да бави останалите.
    const monitorRequest = await monitorWarmupRequest();
    if (monitorRequest) await warmPath(monitorRequest, mirror);
  } finally {
    warmupInFlight = false;
  }
}

/**
 * Един тих `GET` през облака, ако касата още не е виждала пътя, и чакане на
 * огледалния запис. Облачен отказ не се повтаря (правото няма да се появи до
 * следващия вход); облачен успех без потвърден запис — още веднъж.
 */
async function warmPath(
  request: WarmupRequest,
  mirror: Record<string, string> | undefined,
): Promise<void> {
  if (mirroredPaths.has(request.path)) return;
  if (isMirrored(mirror, request.path)) {
    mirroredPaths.add(request.path);
    return;
  }
  for (let attempt = 1; attempt <= MIRROR_ATTEMPTS_PER_WARMUP; attempt += 1) {
    // Режимът може да се е сменил, докато е текло чакането за самоличността
    // или предишният опит — в локален режим няма какво да се огледва
    // (четенето идва от касата).
    if (isEdgeLocalMode()) return;
    let mirrorWrite: Promise<boolean> | undefined;
    try {
      const response = await api.get(request.path, { params: request.params });
      mirrorWrite = response.config.edgeMirrorWrite;
    } catch (error) {
      // Липсващо право или стар BE: пътят остава за следващия вход.
      devLog("mirror warmup failed", request.path, error);
      return;
    }
    if (mirrorWrite && (await mirrorWrite)) {
      mirroredPaths.add(request.path);
      devLog("mirror warmup", request.path);
      return;
    }
    devLog("mirror warmup — the till did not store", request.path, attempt);
  }
}

/** Само за тестове: забравя потвърденото в това „зареждане". */
export function resetEdgeWarmupForTests() {
  mirroredPaths.clear();
  warmupInFlight = false;
}
